import { Row, Section } from "anketa-core/group";
import { MessageCollection, Translation } from "anketa-core/i18n";
import { OpenQuestion } from "anketa-core/open_question";
import { NumericQuestion } from "anketa-core/numeric_question";
import { OptionsQuestion } from "anketa-core/options_question";
import { TextValidator } from "anketa-core/validation";
import { TextValidatorType } from "anketa-core/base";
import {
  AnketaCheckPermissionOptions,
  AnketaResourcesEnum,
  extractValue,
} from "../../../../";
import { calculateHostIP, getMaxHostOffset } from "../../../../helper/ip";
import { QuestionsRightsTypes } from "..";

/**
 * Global option list for the per-probe storage media type. The fact value
 * stored is the option key ("ssd" / "nvme"); `getProbes` maps it to the shared
 * media-type union.
 */
export const PROBE_STORAGE_MEDIA_OPTIONS =
  "default.options.probe_storage_media";
MessageCollection.Global.setTranslations(`${PROBE_STORAGE_MEDIA_OPTIONS}.ssd`, [
  new Translation("en", "SSD"),
  new Translation("de", "SSD"),
]);
MessageCollection.Global.setTranslations(`${PROBE_STORAGE_MEDIA_OPTIONS}.nvme`, [
  new Translation("en", "NVMe"),
  new Translation("de", "NVMe"),
]);

/**
 * Reads a previously stored probe field value from the flattened `oldValues`
 * map. Probe answers are keyed as
 * `networks.probes.probe_<idx>.probe_<idx>_<field>` (mirroring the agents
 * convention `networks.agents.agent_<idx>.agent_<idx>_<field>`).
 */
const getProbeFieldValue = (
  oldValues: any,
  probeIdx: number,
  field: string,
): string => {
  if (!oldValues || typeof oldValues !== "object") {
    return "";
  }
  const key = `networks.probes.probe_${probeIdx}.probe_${probeIdx}_${field}`;
  const raw = oldValues[key];
  if (raw === undefined || raw === null) {
    return "";
  }
  const value = typeof raw === "object" ? extractValue(raw) : raw;
  return value ?? "";
};

/**
 * Generates the question Row for a single probe.
 *
 * The CPE and Transfer IPs are derived (read-only) and allocated from the TOP
 * of the CIDRs downwards (highest usable host first), while the agents allocate
 * from the bottom upwards. This keeps agent and probe IPs in disjoint ends of
 * the same CIDR pools, so they can never collide regardless of the agent/probe
 * counts. The host offsets are computed by the caller (`generateProbes`).
 *
 * The customer-facing network details (location, CIDR, VLAN, IP) are editable
 * by external users, while the hardware identifiers (MAC addresses, serial
 * number) are internal-only - matching how the agents section hides MAC/SN
 * from external users.
 */
const generateProbe = (
  probeIdx: number,
  cpeCidr: string,
  cpeHostOffset: number | undefined,
  transferCidr: string,
  transferHostOffset: number | undefined,
  questionsRights: (
    q: QuestionsRightsTypes,
    options?: AnketaCheckPermissionOptions<AnketaResourcesEnum.questionnaires>,
  ) => void,
  oldValues?: any,
): Row => {
  const uid = `probe_${probeIdx}`;
  const baseGroup = new Row(uid, [
    new Translation("en", `Probe ${probeIdx}`),
    new Translation("de", `Probe ${probeIdx}`),
  ]);

  const locationName = new OpenQuestion(`probe_${probeIdx}_location_name`, [
    new Translation("en", "Location Name"),
    new Translation("de", "Standortname"),
  ]);
  questionsRights(locationName);
  locationName.validator = new TextValidator();
  locationName.setFactValue(
    getProbeFieldValue(oldValues, probeIdx, "location_name"),
  );
  baseGroup.addChild(locationName);

  const cpeIp =
    cpeCidr && cpeHostOffset !== undefined && cpeHostOffset > 0
      ? calculateHostIP(cpeCidr, cpeHostOffset)
      : "";
  const transferIp =
    transferCidr && transferHostOffset !== undefined && transferHostOffset > 0
      ? calculateHostIP(transferCidr, transferHostOffset)
      : "";

  const cpeIpField = new OpenQuestion(`probe_${probeIdx}_cpe`, [
    new Translation("en", "CPE IP address"),
    new Translation("de", "CPE IP-Adresse"),
  ]);
  questionsRights(cpeIpField);
  cpeIpField.setFactValue(cpeIp);
  cpeIpField.readonly = true;
  baseGroup.addChild(cpeIpField);

  const transferIpField = new OpenQuestion(`probe_${probeIdx}_transfer`, [
    new Translation("en", "Transfer IP address"),
    new Translation("de", "Transfer IP-Adresse"),
  ]);
  questionsRights(transferIpField);
  transferIpField.setFactValue(transferIp);
  transferIpField.readonly = true;
  baseGroup.addChild(transferIpField);

  const customerCidr = new OpenQuestion(`probe_${probeIdx}_customer_cidr`, [
    new Translation("en", "Customer CIDR"),
    new Translation("de", "Kunden CIDR"),
  ]);
  questionsRights(customerCidr);
  customerCidr.validator = new TextValidator(TextValidatorType.CIDR);
  customerCidr.setFactValue(
    getProbeFieldValue(oldValues, probeIdx, "customer_cidr"),
  );
  baseGroup.addChild(customerCidr);

  const customerVlan = new OpenQuestion(`probe_${probeIdx}_customer_vlan`, [
    new Translation("en", "Customer VLAN"),
    new Translation("de", "Kunden VLAN"),
  ]);
  questionsRights(customerVlan);
  customerVlan.validator = new TextValidator(TextValidatorType.VLAN);
  customerVlan.setFactValue(
    getProbeFieldValue(oldValues, probeIdx, "customer_vlan"),
  );
  baseGroup.addChild(customerVlan);

  const customerIp = new OpenQuestion(`probe_${probeIdx}_customer_ip`, [
    new Translation("en", "Customer IP address"),
    new Translation("de", "Kunden IP-Adresse"),
  ]);
  questionsRights(customerIp);
  customerIp.validator = new TextValidator(TextValidatorType.IP);
  customerIp.setFactValue(getProbeFieldValue(oldValues, probeIdx, "customer_ip"));
  baseGroup.addChild(customerIp);

  const customerGw = new OpenQuestion(`probe_${probeIdx}_customer_gw`, [
    new Translation("en", "Gateway"),
    new Translation("de", "Gateway"),
  ]);
  questionsRights(customerGw);
  customerGw.validator = new TextValidator(TextValidatorType.IP);
  customerGw.setFactValue(getProbeFieldValue(oldValues, probeIdx, "customer_gw"));
  baseGroup.addChild(customerGw);

  const customerNtp = new OpenQuestion(`probe_${probeIdx}_customer_ntp`, [
    new Translation("en", "NTP Server"),
    new Translation("de", "NTP-Server"),
  ]);
  questionsRights(customerNtp);
  customerNtp.validator = new TextValidator(TextValidatorType.IP);
  customerNtp.setFactValue(
    getProbeFieldValue(oldValues, probeIdx, "customer_ntp"),
  );
  baseGroup.addChild(customerNtp);

  const customerDns = new OpenQuestion(`probe_${probeIdx}_customer_dns`, [
    new Translation("en", "DNS Server"),
    new Translation("de", "DNS-Server"),
  ]);
  questionsRights(customerDns);
  customerDns.validator = new TextValidator(TextValidatorType.IP);
  customerDns.setFactValue(
    getProbeFieldValue(oldValues, probeIdx, "customer_dns"),
  );
  baseGroup.addChild(customerDns);

  const macAddressOne = new OpenQuestion(`probe_${probeIdx}_mac_one`, [
    new Translation("en", "MAC-Address (First NIC)"),
    new Translation("de", "MAC-Adresse (Erster NIC)"),
  ]);
  questionsRights(macAddressOne, {
    isInternal: true,
    isExternal: false,
  });
  macAddressOne.validator = new TextValidator(TextValidatorType.MacAddress);
  macAddressOne.setFactValue(getProbeFieldValue(oldValues, probeIdx, "mac_one"));
  baseGroup.addChild(macAddressOne);

  const macAddressTwo = new OpenQuestion(`probe_${probeIdx}_mac_two`, [
    new Translation("en", "MAC-Address (Second NIC)"),
    new Translation("de", "MAC-Adresse (Zweiter NIC)"),
  ]);
  questionsRights(macAddressTwo, {
    isInternal: true,
    isExternal: false,
  });
  macAddressTwo.validator = new TextValidator(TextValidatorType.MacAddress);
  macAddressTwo.setFactValue(getProbeFieldValue(oldValues, probeIdx, "mac_two"));
  baseGroup.addChild(macAddressTwo);

  const serialnumber = new OpenQuestion(`probe_${probeIdx}_sn`, [
    new Translation("en", "Serial Number"),
    new Translation("de", "Seriennummer"),
  ]);
  questionsRights(serialnumber, {
    isInternal: true,
    isExternal: false,
  });
  serialnumber.validator = new TextValidator();
  serialnumber.setFactValue(getProbeFieldValue(oldValues, probeIdx, "sn"));
  baseGroup.addChild(serialnumber);

  const storageCount = new NumericQuestion(`probe_${probeIdx}_storage_count`, [
    new Translation("en", "Number of Storage Devices"),
    new Translation("de", "Anzahl Speichermedien"),
  ]);
  questionsRights(storageCount, {
    isInternal: true,
    isExternal: false,
  });
  const storedStorageCount = getProbeFieldValue(
    oldValues,
    probeIdx,
    "storage_count",
  );
  if (storedStorageCount !== "") {
    storageCount.setFactValue(storedStorageCount);
  }
  baseGroup.addChild(storageCount);

  const storageType = new OptionsQuestion(
    `probe_${probeIdx}_storage_type`,
    [
      new Translation("en", "Storage Type"),
      new Translation("de", "Speichertyp"),
    ],
    PROBE_STORAGE_MEDIA_OPTIONS,
  );
  questionsRights(storageType, {
    isInternal: true,
    isExternal: false,
  });
  storageType.setFactValue(
    getProbeFieldValue(oldValues, probeIdx, "storage_type"),
  );
  baseGroup.addChild(storageType);

  return baseGroup;
};

/**
 * Builds the "Probes" Section containing one Row per probe. The number of
 * probes is driven by the `number_of_probes` numeric question; like the
 * agents section, this is re-generated (and re-attached to the parent under
 * the stable `probes` id) whenever the count changes.
 *
 * Probe IPs share the CPE (A/B) and Transfer CIDR pools with the agents, but
 * are allocated from the TOP of each CIDR downwards (the agents allocate from
 * the bottom upwards). Probe `j` (1-based, `p = j - 1`):
 *   - alternates CPE CIDR A (even `p`) / CIDR B (odd `p`), like the agents, and
 *     takes the `floor(p / 2)`-th host down from the top of that CIDR;
 *   - takes the `p`-th host down from the top of the Transfer CIDR.
 * Because agents grow up from the bottom and probes grow down from the top,
 * their CPE/Transfer IPs are never duplicated, independent of the counts.
 */
export const generateProbes = (
  probeCount: number,
  cpeCidrA: string,
  cpeCidrB: string,
  transferCidr: string,
  questionsRights: (
    q: QuestionsRightsTypes,
    options?: AnketaCheckPermissionOptions<AnketaResourcesEnum.questionnaires>,
  ) => void,
  oldValues?: any,
): Section => {
  const probes = new Section("probes", [
    new Translation("en", "Probes"),
    new Translation("de", "Probes"),
  ]);
  questionsRights(probes);

  const cpeMaxA = getMaxHostOffset(cpeCidrA);
  const cpeMaxB = getMaxHostOffset(cpeCidrB);
  const transferMax = getMaxHostOffset(transferCidr);

  if (typeof probeCount === "number" && !isNaN(probeCount) && probeCount > 0) {
    for (let i = 1; i < probeCount + 1; i++) {
      const p = i - 1;
      // Alternate CPE CIDR A (even p) / B (odd p), like the agents.
      const useCidrA = p % 2 === 0;
      const cpeCidr = useCidrA ? cpeCidrA : cpeCidrB;
      const cpeMax = useCidrA ? cpeMaxA : cpeMaxB;
      const rankOnCidr = Math.floor(p / 2);
      const cpeHostOffset =
        cpeMax !== undefined ? cpeMax - rankOnCidr : undefined;
      const transferHostOffset =
        transferMax !== undefined ? transferMax - p : undefined;
      probes.addChild(
        generateProbe(
          i,
          cpeCidr,
          cpeHostOffset,
          transferCidr,
          transferHostOffset,
          questionsRights,
          oldValues,
        ),
      );
    }
  }

  return probes;
};
